import { Component, OnInit, Input, OnDestroy, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, Observable } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';

import { Folder } from '../../../../../Monitor/Shared/models/folder.model';

import { Mention } from '../../../../Mentions/models/mention';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { AbstractFolderService } from '../../../services/abstract-folder.service';
import { AbstractMentionsService } from '../../../services/abstract-mentions.service';


@Component({
    selector: 'agpr-social-post-folders',
    templateUrl: './social-post-folders.component.html',
    styleUrls: ['./social-post-folders.component.scss'],
    standalone: false
})
export class SocialPostFoldersComponent implements OnInit, OnDestroy {

    @Input() mention: Mention;

    readonly defaultFoldersToShow: number = 3;
    readonly buttonTypes = ButtonTypesEnum;

    public folders: Folder[];
    public foldersToShow: number = this.defaultFoldersToShow;
    public shownFolders: Folder[];

    private subscriptions: Subscription[] = [];

    constructor(
        @Inject('MentionsService') private mentionsService: AbstractMentionsService,
        @Inject('FolderService') private folderService: AbstractFolderService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.subscriptions.push(
            this.mentionsService.getFocusedMention().pipe(
                switchMap((mention) => this.setupMentionFolders(mention))
            ).subscribe(),
        );
    }

    ngOnInit() {
        this.setupMentionFolders(this.mention).subscribe();
    }

    private setupMentionFolders(mention: Mention): Observable<Folder[]> {
        return this.folderService.getFoldersByMention(mention).pipe(
            tap(folders => {
                this.folders = folders;
                this.folders.sort(
                    (a, b) => {
                        const bSortDate = b.modifiedDate ? +b.modifiedDate : +b.createdDate;
                        const aSortDate = a.modifiedDate ? +a.modifiedDate : +a.createdDate;
                        return bSortDate - aSortDate;
                    });
                this.updateShownFolders();
            }),
        );
    }

    /**
     * Displays the full list of folders.
     */
    viewMoreFolders(): void {
        this.foldersToShow = this.folders.length;
        this.updateShownFolders();
    }

    /**
     * Hides excess folders.
     */
    viewLessFolders(): void {
        this.foldersToShow = this.defaultFoldersToShow;
        this.updateShownFolders();
    }

    /**
     * Updates the shown folders list.
     */
    updateShownFolders(): void {
        if (this.folders) {
            this.shownFolders = this.folders.slice(0, this.foldersToShow);
        }
    }

    /**
     * Removes the mention from the provided folder
     */
    removeMentionFromFolder(folder: Folder): void {
        if (this.mention) {
            this.folderService.removeMentionsFromFolder([this.mention], folder).subscribe();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
